// @flow
import React from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SimpleBar from 'simplebar-react';
import { ProjectActions } from 'modules/project/actions';
import { ProjectSelector } from 'modules/project/selector';
import { BedsFilter } from 'components/molecules/bedsFilter';
import { LocationFilter } from 'components/molecules/locationFilter';
import { ProjectStatusFilter } from 'components/molecules/projectStatusFilter';
import { PropertyTypeFilter } from 'components/molecules/propertyTypeFilter';
import { useViewport } from 'components/templates/viewportProvider';
import chevronRightIcon from 'assets/images/rightb.png';
import type {
  ActiveProjectFilters,
  LocationCityOption,
  ProjectFilters,
  ProjectStatusOption,
  PropertyTypeOption,
} from 'types/filter';
// eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  isOpen: boolean,
  onClosePress: () => void,
  filters: ProjectFilters | null,
  activeFilters: ActiveProjectFilters | null,
  toggleProjectFilter: (type: string, value: number) => void,
  clearProjectFilters: () => void,
};

const ProjectFiltersModal = (props: Props) => {
  const { isMobile } = useViewport();
  const {
    isOpen,
    onClosePress,
    filters,
    activeFilters,
    toggleProjectFilter,
    clearProjectFilters,
  } = props;
  if (!filters) {
    return null;
  }

  const onClose = () => {
    onClosePress();
  };

  const toggleLocation = (option: LocationCityOption) => {
    toggleProjectFilter('location', option.id);
  };

  const toggleProjectStatus = (option: ProjectStatusOption) => {
    toggleProjectFilter('projectStatus', option.id);
  };

  const togglePropertyType = (option: PropertyTypeOption) => {
    toggleProjectFilter('propertyType', option.id);
  };

  const changeBeds = (value: Array<number>) => {
    toggleProjectFilter('beds', value[0]);
  };

  const clearAllFilters = e => {
    e.preventDefault();
    clearProjectFilters();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClosePress}
      overlayClassName={'modal-overlay normal-scroll'}
      className={`project-filters-overlay-content ${isMobile ? 'mobile' : ''}`}
    >
      <div className={'project-filters-modal'}>
        <a className={'collapse-icon-container'} onClick={onClose}>
          <img src={chevronRightIcon} alt={'icon'} />
        </a>
        {!isMobile && <span className={'modal-title'}>Filters</span>}
        <a className={'modal-button'} onClick={clearAllFilters} href={'#'}>
          Clear All
        </a>
        <SimpleBar autoHide={true} style={{ height: '100%' }}>
          <div className={'project-filters-content'}>
            <div className={'filters-header'}>
              <h3>Looking for something specific?</h3>
              {!isMobile && (
                <p>Filter by location, project status, and property type.</p>
              )}
            </div>
            <div className={'filter-set'}>
              <LocationFilter
                activeFilters={activeFilters.location}
                toggleFilter={city => toggleLocation(city)}
                options={filters.location}
              />
              <ProjectStatusFilter
                activeFilters={activeFilters.projectStatus}
                toggleFilter={status => toggleProjectStatus(status)}
                options={filters.projectStatus}
              />
              <PropertyTypeFilter
                activeFilters={activeFilters.propertyType}
                toggleFilter={type => togglePropertyType(type)}
                options={filters.propertyType}
              />
              <BedsFilter
                min={filters.beds.min}
                max={filters.beds.max}
                values={activeFilters.beds ? [activeFilters.beds] : null}
                onChange={value => changeBeds(value)}
              />
            </div>
          </div>
        </SimpleBar>
      </div>
    </Modal>
  );
};

const mapStateToProps = state => {
  return {
    filters: ProjectSelector.getProjectFilters(state),
    activeFilters: ProjectSelector.getActiveProjectFilters(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { toggleProjectFilter, clearProjectFilters } = ProjectActions;
  return bindActionCreators(
    {
      toggleProjectFilter,
      clearProjectFilters,
    },
    dispatch
  );
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectFiltersModal);

export { connectedComponent as ProjectFiltersModal };
